import { addFullStoryInfo } from './add-full-story-info.js';
const isBrowser = typeof window !== 'undefined';
const isNode = !isBrowser;
function addFilterInfoToNoticeContext(notice, info) {
    if (!notice.context) {
        notice.context = { filters: {} };
    }
    else if (!notice.context.filters) {
        notice.context.filters = {};
    }
    notice.context.filters = {
        ...notice.context.filters,
        ...info
    };
    return notice;
}
function noticeWithFilterInfo(notice, filterInfo) {
    return Object.keys(filterInfo).length > 0 ?
        addFilterInfoToNoticeContext(notice, filterInfo) :
        notice;
}
/**
 * Creates a filter that submits only `options.sampleRatePercentage` of the notices with a message
 * matching `options.messagePattern` if `options.env` is the same environment as the execution environment.
 * It does not prevent from submitting the notice if it does not match `options.messagePattern` or
 * `options.env` is not the same environment as the execution environment.
 *
 * @param options
 */
export default function createFilter(options) {
    const { env, messagePattern, sampleRatePercentage, backtracePattern } = options;
    let { backtracePatternMatch } = options;
    const filterInfo = {};
    return (notice) => {
        var _a, _b;
        // Process env
        if (env) {
            if (isBrowser && env === 'node' || isNode && env === 'browser') {
                return noticeWithFilterInfo(notice, filterInfo); // do not process notice any further if execution environment does not match the requested env
            }
            else {
                filterInfo.env = env;
            }
        }
        if (isBrowser) {
            addFullStoryInfo(filterInfo);
        }
        // Process messagePattern
        if (messagePattern) {
            const errorMessage = notice.errors && notice.errors[0] && notice.errors[0].message;
            if (errorMessage) {
                const messagePatterns = Array.isArray(messagePattern) ? messagePattern : [messagePattern];
                let matchingPattern = null;
                messagePatterns.some(pattern => {
                    if (pattern instanceof RegExp && pattern.test(errorMessage) || // regex
                        typeof pattern === 'string' && errorMessage.includes(pattern) // string
                    ) {
                        matchingPattern = `${pattern}`;
                        return true;
                    }
                    return false;
                });
                if (!matchingPattern) { // do not prevent from submitting the notice if no matching pattern
                    return noticeWithFilterInfo(notice, filterInfo);
                }
                else {
                    filterInfo.messagePattern = matchingPattern;
                }
            }
        }
        if (backtracePattern) {
            backtracePatternMatch !== null && backtracePatternMatch !== void 0 ? backtracePatternMatch : (backtracePatternMatch = 'any');
            let isBacktraceMatching;
            (_b = (_a = notice.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.backtrace.some(frame => {
                if (backtracePattern instanceof RegExp && backtracePattern.test(frame.file) || // regex
                    typeof backtracePattern === 'string' && frame.file.includes(backtracePattern) // string
                ) { // backtrace line matches
                    if (backtracePatternMatch === 'any') {
                        isBacktraceMatching = true; // at least one backtrace line matches, thus the notice should be filtered
                        return true; // stop iterating over the rest of the backtrace lines
                    }
                    else if (backtracePatternMatch === 'all') {
                        isBacktraceMatching !== null && isBacktraceMatching !== void 0 ? isBacktraceMatching : (isBacktraceMatching = true);
                        isBacktraceMatching = isBacktraceMatching && true;
                        return false; // We need to check all backtrace lines to see if all of them match
                    }
                }
                else { // backtrace line does not match
                    if (backtracePatternMatch === 'all') {
                        isBacktraceMatching = false;
                    }
                }
                return false;
            });
            isBacktraceMatching !== null && isBacktraceMatching !== void 0 ? isBacktraceMatching : (isBacktraceMatching = false);
            if (isBacktraceMatching) {
                filterInfo.backtrace = {
                    pattern: `${backtracePattern}`,
                    match: backtracePatternMatch
                };
            }
            else { // backtracePattern does not match any backtrace line: submit the notice
                return noticeWithFilterInfo(notice, filterInfo);
            }
        }
        // Process sampleRatePercentage
        if (sampleRatePercentage !== undefined) {
            if (sampleRatePercentage === 0)
                return null;
            if (Math.random() <= sampleRatePercentage / 100) { // Only sampleRatePercentage % of notices are submitted to Airbrake
                // Attach filter info to notice so that we can have a realistic guess in Airbrake on how many of this type of error is thrown
                filterInfo.sampleRatePercentage = sampleRatePercentage;
            }
            else {
                return null; // Not sending the Notice to Airbrake
            }
        }
        return noticeWithFilterInfo(notice, filterInfo);
    };
}



// WEBPACK FOOTER //
// ./node_modules/@doxo/willow-airbrake/dist/esm/filters/create-filter.js